import React from 'react';
import { 
  Edit, 
  FileText, 
  User, 
  ArrowLeftRight, 
  HelpCircle, 
  Clock,
  ChevronDown,
  AlertCircle,
  ChevronLeft
} from 'lucide-react';

const RequiredFieldMarker = () => (
  <span className="text-red-500 ml-1 dark:text-red-400" aria-label="required field">*</span>
);

function CreateDCQForm({ dcqData, errors, displayParticipantId, handleInputChange, handleSubmit }) {
  // DCQ type options
  const dcqTypes = [
    'Query',
    'Deviation', 
    'Protocol Violation',
    'Data Clarification',
    'Missing Data',
    'Other'
  ];

  const fieldConfigs = [
    { 
      name: 'type', 
      icon: Edit,
      type: 'select',
      options: dcqTypes,
      placeholder: 'Select DCQ type'
    },
    { 
      name: 'action', 
      icon: FileText,
      type: 'text',
      placeholder: 'e.g., Update, Delete, Clarify'
    },
    { 
      name: 'initial_value', 
      icon: Clock,
      type: 'text',
      placeholder: 'Enter the current value'
    },
    { 
      name: 'new_value', 
      icon: ArrowLeftRight,
      type: 'text',
      placeholder: 'Enter the proposed value'
    },
  ];

  const textAreaConfigs = [
    { 
      name: 'requested_change', 
      icon: Edit,
      placeholder: 'Describe the specific change being requested',
      rows: 3
    },
    { 
      name: 'reason_for_change', 
      icon: HelpCircle,
      placeholder: 'Explain why this change is necessary',
      rows: 4
    },
  ];

  const hasErrors = Object.keys(errors).length > 0;

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit(e);
  };

  return (
    <div className="space-y-6">
      {/* Error Summary */}
      {hasErrors && (
        <div className="flex items-start gap-3 rounded-lg border border-danger-100 bg-danger-50 p-4 dark:border-danger-500/30 dark:bg-danger-500/10">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-danger-600 dark:text-danger-400" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-danger-800 dark:text-danger-400">Please correct the following errors:</h3>
            <ul className="mt-2 list-inside list-disc text-sm text-danger-700 dark:text-danger-400">
              {Object.entries(errors).map(([field, error]) => (
                <li key={field}>{error}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Form Fields Grid */}
      <div className="space-y-6">
        {/* Two-column grid for main fields */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {fieldConfigs.map(({ name, icon: Icon, type, options, placeholder }) => (
            <div key={name} className="relative">
              <label htmlFor={name} className="label">
                {name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                <RequiredFieldMarker />
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2">
                  <Icon className="text-gray-400 dark:text-gray-500" size={16} />
                </div>
                
                {type === 'select' ? (
                  <>
                    <select
                      id={name}
                      name={name}
                      required
                      className={`input cursor-pointer appearance-none pl-10 pr-10 ${errors[name] ? 'input-error' : ''}`}
                      value={dcqData[name]}
                      onChange={handleInputChange}
                    >
                      <option value="">{placeholder}</option>
                      {options.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                      <ChevronDown className="text-gray-400 dark:text-gray-500" size={16} />
                    </div>
                  </>
                ) : (
                  <input
                    type={type}
                    id={name}
                    name={name}
                    required
                    className={`input pl-10 ${errors[name] ? 'input-error' : ''}`}
                    placeholder={placeholder}
                    value={dcqData[name]}
                    onChange={handleInputChange}
                  />
                )}
              </div>
              {errors[name] && (
                <p className="field-error flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {errors[name]}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Full-width text areas */}
        <div className="space-y-6">
          {textAreaConfigs.map(({ name, icon: Icon, placeholder, rows }) => (
            <div key={name} className="relative">
              <label htmlFor={name} className="label">
                {name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                <RequiredFieldMarker />
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute left-3 top-3 z-10">
                  <Icon className="text-gray-400 dark:text-gray-500" size={16} />
                </div>
                <textarea
                  id={name}
                  name={name}
                  rows={rows}
                  required
                  className={`input resize-none pl-10 ${errors[name] ? 'input-error' : ''}`}
                  placeholder={placeholder}
                  value={dcqData[name]}
                  onChange={handleInputChange}
                />
              </div>
              {errors[name] && (
                <p className="field-error flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {errors[name]}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Participant ID Field - Read Only */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
          <label htmlFor="participant" className="label">
            Participant ID
          </label>
          <div className="relative">
            <div className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2">
              <User className="text-gray-400 dark:text-gray-500" size={16} />
            </div>
            <input
              type="text"
              name="participant"
              id="participant"
              value={displayParticipantId}
              readOnly
              className="input cursor-not-allowed pl-10 text-gray-600 dark:text-gray-300"
            />
          </div>
          <p className="hint">This field is automatically populated and cannot be edited</p>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 border-t border-gray-100 pt-6 dark:border-gray-700">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleFormSubmit}
          className="btn btn-primary"
        >
          Create DCQ
        </button>
      </div>
    </div>
  );
}

// Demo wrapper to show the complete page
export const CreateDCQPage = () => {
  const formProps = {
    dcqData: {
      type: '',
      action: '',
      participant: '123',
      initial_value: '',
      new_value: '',
      requested_change: '',
      reason_for_change: '',
    },
    errors: {},
    displayParticipantId: 'P-2024-001',
    handleInputChange: (e) => console.log('Input changed:', e.target.name, e.target.value),
    handleSubmit: (e) => {
      console.log('Form submitted');
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-1">
        <button
          onClick={() => window.history.back()}
          className="back-link"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Back</span>
        </button>
      </div>

      <div className="card overflow-hidden">
        <div className="card-band">
          <div>
            <h2 className="card-band-title">Create New DCQ</h2>
            <p className="card-band-description">Fill out the form below to create a new Data Clarification Query</p>
          </div>
        </div>
        <div className="p-6">
          <CreateDCQForm {...formProps} />
        </div>
      </div>
    </div>
  );
};

export default CreateDCQForm;